/** German labels for the English domain codes (ubiquitous language, see CONTEXT.md). */
import type {
  AlternativeFinancing,
  ApplicantType,
  BodyType,
  BudgetKind,
  CumulationKind,
  DeadlineKind,
  ExclusionReason,
  FundingLevel,
  FundingType,
  PreliminaryRating,
  RateKind,
  Quadrant,
  Rating,
  PlatformHint,
  Suitability,
  SuitabilitySource,
  TenantStage,
} from '../api/types'

export type Status = 'success' | 'info' | 'warning' | 'danger'

export const RATING: Record<Rating, { label: string; status: Status; range: string }> = {
  VERY_HIGH: { label: 'sehr hoch', status: 'success', range: '≥ 4,5' },
  HIGH: { label: 'hoch', status: 'info', range: '≥ 3,5 und < 4,5' },
  MEDIUM: { label: 'mittel', status: 'warning', range: '≥ 2,5 und < 3,5' },
  LOW: { label: 'gering', status: 'danger', range: '< 2,5' },
}
export const RATING_OPEN = 'offen'

export const QUADRANT: Record<Quadrant, { label: string; short: string; hint: string }> = {
  ACT_AND_APPLY: {
    label: 'Sofort umsetzen & aktive Bewerbung zur Förderung',
    short: 'Umsetzen & bewerben',
    hint: 'Hohe Priorität, gute Förderperspektive',
  },
  CLARIFY_POLITICALLY: {
    label: 'Politisch klären & potenzielle Alternativen prüfen',
    short: 'Politisch klären',
    hint: 'Hohe Priorität, eingeschränkte Förderfähigkeit',
  },
  RECONSIDER_PRIORITY: {
    label: 'Prüfung der Repriorisierung zur Nutzung von Förderchancen',
    short: 'Repriorisierung prüfen',
    hint: 'Geringe Priorität, gute Förderperspektive',
  },
  DEFER: {
    label: 'Zurückstellen',
    short: 'Zurückstellen',
    hint: 'Geringe Priorität oder geringe Förderchancen',
  },
}

export const ALTERNATIVE_FINANCING: Record<AlternativeFinancing, string> = {
  NO_FUNDING_REQUIRED: 'Keine Fördermittel erforderlich',
  ALTERNATIVE_AVAILABLE: 'Alternative Finanzierbarkeit gegeben',
  HIGH_NEED: 'Hoher Fördermittelbedarf',
  NOT_FEASIBLE_WITHOUT_FUNDING: 'Ohne Fördermittel nicht umsetzbar',
}

export const PRELIMINARY_RATING: Record<PreliminaryRating, { label: string; status: Status }> = {
  HIGH: { label: 'Hoch', status: 'success' },
  MEDIUM: { label: 'Mittel', status: 'warning' },
  LOW: { label: 'Gering', status: 'danger' },
}

export const BUDGET_KIND: Record<BudgetKind, string> = {
  ACTUAL: 'Ergebnis',
  APPROPRIATION: 'Ansatz',
  PLANNED: 'Planwert',
  COMMITMENT_AUTHORIZATION: 'Verpflichtungsermächtigung (VE)',
}

/** Förderebene; the key order is the fixed display order EU > Bund > Land > Kommune > Privat. */
export const FUNDING_LEVEL: Record<FundingLevel, string> = {
  EU: 'EU',
  FEDERAL: 'Bund',
  STATE: 'Land',
  MUNICIPAL: 'Kommune',
  PRIVATE: 'Private Mittel',
}

/** Antragsberechtigte: catalog from the municipalities' point of view (ticket 10). */
export const APPLICANT_TYPE: Record<ApplicantType, string> = {
  MUNICIPALITY: 'Gemeinden und Städte',
  DISTRICT: 'Kreise',
  ASSOCIATION: 'Ämter, Gemeindeverbände und Zweckverbände',
  MUNICIPAL_COMPANY: 'kommunale Unternehmen und Eigenbetriebe',
  NON_PROFIT: 'Vereine und gemeinnützige Organisationen',
  COMPANY: 'Unternehmen',
  OTHER: 'Sonstige',
}

/** Körperschaftsart of a municipality, set by the platform administration. */
export const BODY_TYPE: Record<BodyType, string> = {
  MUNICIPALITY: 'Gemeinde oder Stadt',
  DISTRICT: 'Kreis',
  ASSOCIATION: 'Amt, Gemeindeverband oder Zweckverband',
}

export const FUNDING_TYPE: Record<FundingType, string> = {
  GRANT: 'Zuschuss',
  LOAN: 'Darlehen',
  OTHER: 'Sonstige',
}

/** Kumulierung (ticket 06). */
export const CUMULATION_KIND: Record<CumulationKind, string> = {
  POSSIBLE: 'möglich',
  EXCLUDED: 'ausgeschlossen',
  UNSPECIFIED: 'keine Angabe',
}

/** Art des Fördersatzes (ticket 05). */
export const RATE_KIND: Record<RateKind, string> = {
  FIXED: 'festgelegt',
  UP_TO: 'bis zu',
  OTHER: 'andere Regelung',
}

/** Fristart of an Antragsfrist (ticket 03). */
export const DEADLINE_KIND: Record<DeadlineKind, string> = {
  ONGOING: 'laufend',
  CUTOFF: 'Stichtag',
  ANNUAL: 'jährlich',
  WINDOW: 'Einreichungsfenster',
  OTHER: 'sonstige',
}

export const SUITABILITY: Record<Suitability, { label: string; status: Status | 'neutral' }> = {
  SUITABLE: { label: 'passt', status: 'success' },
  NOT_SUITABLE: { label: 'passt nicht', status: 'danger' },
  OPEN: { label: 'offen', status: 'neutral' },
}

export const SUITABILITY_SOURCE: Record<SuitabilitySource, string> = {
  MANUAL: 'Entscheidung',
  SYSTEM: 'Systemvorschlag',
  NONE: '',
}

export const EXCLUSION_REASON: Record<ExclusionReason, string> = {
  NOT_ELIGIBLE_APPLICANT: 'Nicht antragsberechtigt',
  DEADLINE_PASSED: 'Frist verstrichen',
  MEASURE_NOT_ELIGIBLE: 'Maßnahme nicht förderfähig',
  CUMULATION_EXCLUDED: 'Kumulierung ausgeschlossen',
  OTHER: 'Sonstiges',
}

export const TENANT_ROLE: Record<'ADMIN' | 'EDITOR' | 'VIEWER', { label: string; hint: string }> = {
  ADMIN: { label: 'Administration', hint: 'Bewertungsschemata, Themenfelder und Erscheinungsbild der Kommune' },
  EDITOR: { label: 'Bearbeitung', hint: 'Maßnahmen, Förderprogramme und Bewertungen erfassen' },
  VIEWER: { label: 'Lesen', hint: 'Ergebnisse ansehen' },
}

export const PROVISIONAL = {
  label: 'vorläufig',
  hint: 'Nicht alle gewichteten Kriterien sind bewertet – fehlende Punkte zählen wie in der Excel als 0.',
}

export const PROCESS_STEPS = [
  { step: 1, path: '/massnahmen', label: 'Maßnahmen', title: 'Erfassung der geplanten Maßnahmen' },
  { step: 2, path: '/schema/priorisierung', label: 'Schema Priorisierung', title: 'Bewertungsschema zur Priorisierung' },
  { step: 3, path: '/priorisierung', label: 'Priorisierung', title: 'Priorisierung der Maßnahmen' },
  { step: 4, path: '/foerderprogramme', label: 'Förderprogramme', title: 'Förderrecherche' },
  { step: 5, path: '/schema/foerderung', label: 'Schema Förderung', title: 'Bewertungsschema der Förderprogramme' },
  { step: 6, path: '/foerderbewertung', label: 'Förderbewertung', title: 'Bewertung der Förderprogramme' },
  { step: 7, path: '/ergebnis', label: 'Ergebnis', title: 'Zusammenführung und Ergebnis' },
] as const

/** Stand einer Kommune: five stages in process order, the first that applies (CONTEXT.md). */
export const TENANT_STAGE: Record<TenantStage, { label: string; hint: string }> = {
  SETUP: { label: 'Einrichtung', hint: 'Noch keine Administration oder kein aktives Konto' },
  MEASURES: { label: 'Maßnahmen erfassen', hint: 'Noch keine Maßnahme angelegt' },
  PRIORITIZATION: { label: 'Priorisierung', hint: 'Nicht alle Maßnahmen vollständig eingeordnet' },
  FUNDING: { label: 'Förderbewertung', hint: 'Nicht jede Maßnahme hat eine Förderbewertung' },
  READY: { label: 'Ergebnis bereit', hint: 'Alle Maßnahmen eingeordnet und bewertet' },
}

/** Hinweis (Plattform): why the platform administration might look at a municipality. */
export const PLATFORM_HINT: Record<PlatformHint, { label: string; why: string }> = {
  NO_ADMIN: { label: 'Keine Administration', why: 'Niemand in der Kommune hat die Rolle Administration.' },
  INVITATIONS_PENDING: {
    label: 'Einladungen offen',
    why: 'Konten mit Einladung, Passwort noch nicht gesetzt. Ein Ablaufdatum der Einladung gibt es nicht.',
  },
  NO_MEASURE_14_DAYS: {
    label: 'Seit 14 Tagen keine Maßnahme',
    why: 'Vor mehr als 14 Tagen angelegt, aber noch keine Maßnahme erfasst.',
  },
  QUIET_60_DAYS: {
    label: 'Seit 60 Tagen ruhig',
    why: 'Keine fachliche Änderung (Maßnahme, Programm, Bewertung, Schema, Themenfeld) seit 60 Tagen.',
  },
  BODY_TYPE_MISSING: {
    label: 'Körperschaftsart fehlt',
    why: 'Ohne Körperschaftsart greift der Vorschlag „nicht antragsberechtigt“ nicht.',
  },
}
